import React, { useEffect } from 'react';
import Input from '@/components/General/inputs/Input';
import { savePath } from '@/data/paths';
import { publishAutomation } from '@/data/automations';
import { buildPublishPayload, isAutomationConflict } from '@/utils/automationBuilder';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Toast from 'react-native-toast-message';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import ToggleRow from '@/components/General/inputs/ToggleRow';
import { useEditScreenStore } from '@/data/store/useEditScreenStore';
import { redirectToDefault } from '@/utils/constants/linking';
import { useLocalSearchParams, useRouter, Redirect } from 'expo-router';
import ModalWrapper from '@/components/General/ModalWrapper';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import { areStringsPopulated } from '@/utils/InputValidator';

const EMPTY_DRAFT = {};

export default function CreatePathModal() {
    const { id } = useLocalSearchParams();
    const storeKey = id || "new";

    const setEditScreenData = useEditScreenStore(s => s.setEditScreenData);
    const removeEditScreenData = useEditScreenStore(s => s.removeEditScreenData);
    const details = useEditScreenStore(s => s.CreatePathModal?.[storeKey]) || EMPTY_DRAFT;

    const router = useRouter();
    const queryClient = useQueryClient();
    const { data: loggedInUser } = Query.people.loggedInUser();
    const { data: pathToEdit } = Query.paths.byId({ id, options: { enabled: !!id } });

    useEffect(() => {
        if (pathToEdit && !Object.keys(details).length) {
            setEditScreenData("CreatePathModal", storeKey, {
                ...pathToEdit,
                originalValues: pathToEdit,
            });
        }
    }, [pathToEdit]);

    const setDetails = (newDetails) => {
        setEditScreenData("CreatePathModal", storeKey, newDetails);
    };

    // v2 definitions save name/description/isActive through automation_publish
    // (the sole audited write path for v2 metadata; §15.4). Legacy defs keep
    // savePath — direct table writes, no revision semantics.
    const isV2 = pathToEdit?.schemaVersion === 2;
    const savePathMutation = useMutation({
        mutationFn: async (args) => {
            if (isV2) {
                return publishAutomation({
                    pathID: id,
                    payload: buildPublishPayload({
                        path: pathToEdit,
                        name: args.name,
                        description: args.description,
                        isActive: args.isActive,
                    }),
                }).then((result) => ({ ...result, id, name: args.name }));
            }
            return savePath(args);
        },
        onSuccess: (data) => {
            removeEditScreenData("CreatePathModal", storeKey);
            queryClient.invalidateQueries({ queryKey: QueryKeys.paths.all() });
            if (id) {
                queryClient.invalidateQueries({ queryKey: QueryKeys.paths.byId(id) });
            }
            if (!id && data?.id) {
                router.back();
                setTimeout(() => router.push("/automations/" + data.id), 100);
            } else {
                redirectToDefault(router, "CreatePathModal");
            }
        },
        onError: (error) => {
            if (isAutomationConflict(error)) {
                Toast.show({ type: 'error', text2: error.message || 'This automation changed since you opened it. Reload and try again.' });
                queryClient.invalidateQueries({ queryKey: QueryKeys.paths.byId(id) });
            }
        },
    });

    const handleSave = () => {
        if (!areStringsPopulated([details.name])) {
            Toast.show({ type: "error", text2: "Please enter a name for this automation." });
            return;
        }

        const { originalValues, ...cleanDetails } = details;
        savePathMutation.mutate({
            id: id || undefined,
            name: cleanDetails.name,
            description: cleanDetails.description,
            isActive: cleanDetails.isActive ?? true,
            createdBy: !id ? loggedInUser?.id : undefined,
            originalValues,
        });
    };

    // Legacy creation is retired — /automations/create only remains for
    // editing existing definitions (?id=). New automations are always v2.
    // Redirect lives after hooks so hook order stays consistent.
    if (!id) {
        return <Redirect href="/automations/new" />;
    }

    return (
        <ModalWrapper
            title="Edit Automation"
            confirmText="Save"
            loading={savePathMutation.isPending}
            onConfirm={handleSave}
            onCancel={() => removeEditScreenData("CreatePathModal", storeKey)}
        >
            <KeyboardAwareScrollView>
                <Input
                    isNotOnSurface
                    title="Automation Name"
                    placeholder="e.g. New Visitor Follow-Up"
                    value={details.name || ""}
                    onUpdate={(val) => setDetails({ ...details, name: val })}
                />

                <Input
                    isNotOnSurface
                    title="Description"
                    placeholder="Describe the purpose of this automation..."
                    multiline
                    value={details.description || ""}
                    onUpdate={(val) => setDetails({ ...details, description: val })}
                />

                <ToggleRow
                    label="Active"
                    description="When active, this automation can start new runs and its triggers will fire."
                    value={details.isActive ?? true}
                    onValueChange={(val) => setDetails({ ...details, isActive: val })}
                />
            </KeyboardAwareScrollView>
        </ModalWrapper>
    );
}
